// Local, offline draft store for in-progress deliverable submissions.
//
// Unlike localStorage (text only), IndexedDB can hold the actual File/Blob objects the
// consultant attached — so a half-finished submission (deliverable file + some bills + typed
// amounts) survives closing the modal / the tab, and is restored when they come back to that
// milestone. Everything is BEST-EFFORT and wrapped so a failure never breaks the submit flow:
// if IndexedDB is unavailable, every function simply no-ops and the app falls back to the
// existing text-only localStorage draft.

const DB_NAME = 'protrack-drafts'
const STORE = 'deliverable-drafts'

const supported = () => typeof indexedDB !== 'undefined'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

// Save the full draft (text + File objects + preview/edit metadata) under `key`.
// Says whether it actually worked (true/false) so a screen can tell "saved" from "only in memory";
// callers that do not care simply ignore it. A full disk aborts the transaction rather than raising
// an error, so onabort is needed too -- without it the promise would never settle.
export async function saveFileDraft(key, draft) {
  if (!supported()) return false
  try {
    const db = await openDB()
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(draft, key)
      tx.oncomplete = () => res()
      tx.onerror = () => rej(tx.error)
      tx.onabort = () => rej(tx.error)
    })
    return true
  } catch { return false /* best-effort — never block the UI */ }
}

// Load a saved draft, or null if none / on any error.
export async function loadFileDraft(key) {
  if (!supported()) return null
  try {
    const db = await openDB()
    return await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readonly')
      const r = tx.objectStore(STORE).get(key)
      r.onsuccess = () => res(r.result || null)
      r.onerror = () => rej(r.error)
    })
  } catch { return null }
}

// Remove a draft (called after a successful submit, or when the user discards it).
export async function deleteFileDraft(key) {
  if (!supported()) return
  try {
    const db = await openDB()
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(key)
      tx.oncomplete = () => res()
      tx.onerror = () => rej(tx.error)
    })
  } catch { /* ignore */ }
}
